/** Build nested category tree from flat category paths. */

import type { BookmarkListItem, Category, CategoryMeta } from './types';
import { joinCategoryPath, splitCategoryPath } from './category-path';

/** Count bookmarks per category path. */
function countByCategory(bookmarks: BookmarkListItem[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const b of bookmarks) {
    const path = b.category_path || 'Uncategorized';
    counts.set(path, (counts.get(path) ?? 0) + 1);
  }
  return counts;
}

/** Build nested category tree from bookmarks. */
export function buildCategoryTree(
  bookmarks: BookmarkListItem[],
  meta?: CategoryMeta[],
): Category[] {
  const counts = countByCategory(bookmarks);
  const metaMap = new Map<string, CategoryMeta>();
  if (meta) {
    for (const m of meta) metaMap.set(m.path, m);
  }

  // Collect all unique category path segments — from bookmark paths AND
  // category_meta rows, so meta-only (freshly created, empty) categories
  // render with count 0.
  const allPaths = new Set<string>();
  const addWithAncestors = (path: string) => {
    const parts = splitCategoryPath(path);
    let current = '';
    for (const part of parts) {
      current = current ? joinCategoryPath([...splitCategoryPath(current), part]) : part;
      allPaths.add(current);
    }
  };
  for (const path of counts.keys()) addWithAncestors(path);
  for (const path of metaMap.keys()) addWithAncestors(path);

  // Build nodes
  const nodeMap = new Map<string, Category>();
  for (const path of allPaths) {
    const parts = splitCategoryPath(path);
    const m = metaMap.get(path);
    nodeMap.set(path, {
      path,
      name: parts[parts.length - 1],
      count: counts.get(path) ?? 0,
      children: [],
      depth: parts.length - 1,
      icon: m?.icon,
      color: m?.color,
    });
  }

  // Wire parent-child
  const roots: Category[] = [];
  for (const [path, node] of nodeMap) {
    const parts = splitCategoryPath(path);
    if (parts.length <= 1) {
      roots.push(node);
    } else {
      const parentPath = joinCategoryPath(parts.slice(0, -1));
      const parent = nodeMap.get(parentPath);
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }
  }

  // Propagate child counts to parents
  function propagate(node: Category): number {
    let total = node.count;
    for (const child of node.children) {
      total += propagate(child);
    }
    node.count = total;
    return total;
  }
  for (const root of roots) propagate(root);

  // Sort alphabetically
  function sortChildren(nodes: Category[]) {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of nodes) sortChildren(n.children);
  }
  sortChildren(roots);

  return roots;
}

/** Build the sidebar tree from exact server-provided path counts. Counts already
 * include descendants, unlike buildCategoryTree's leaf counts. */
export function buildCategoryTreeFromCounts(
  counts: Record<string, number>,
  meta?: CategoryMeta[],
): Category[] {
  const metaMap = new Map((meta ?? []).map((item) => [item.path, item]));
  const allPaths = new Set([...Object.keys(counts), ...metaMap.keys()]);
  for (const path of [...allPaths]) {
    const parts = splitCategoryPath(path);
    for (let i = 1; i < parts.length; i++) {
      allPaths.add(joinCategoryPath(parts.slice(0, i)));
    }
  }

  const nodes = new Map<string, Category>();
  for (const path of allPaths) {
    const parts = splitCategoryPath(path);
    const itemMeta = metaMap.get(path);
    nodes.set(path, {
      path,
      name: parts[parts.length - 1],
      count: counts[path] ?? 0,
      children: [],
      depth: parts.length - 1,
      icon: itemMeta?.icon,
      color: itemMeta?.color,
    });
  }

  const roots: Category[] = [];
  for (const [path, node] of nodes) {
    const parts = splitCategoryPath(path);
    const parent = parts.length > 1
      ? nodes.get(joinCategoryPath(parts.slice(0, -1)))
      : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sort = (items: Category[]) => {
    items.sort((a, b) => a.name.localeCompare(b.name));
    items.forEach((item) => sort(item.children));
  };
  sort(roots);
  return roots;
}
